import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import tilesService from '../services/tilesService';
import collectionsService from '../services/collectionsService';
import artisticObjectsService from '../../artisticobjects/services/artisticObjectsService';
import placesService from '../../artisticobjects/services/placesService';
import {Tile, TileType} from '../types/Tile';
import {DataTable, DataTableFilterMeta, DataTableRowReorderEvent, DataTableValueArray} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from 'primereact/button';
import {useNavigate} from "react-router-dom";
import {handleError} from "../../../common/utils/errorHandling";
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {AdminButton} from "../../../layout/admin";
import {FilterMatchMode} from "primereact/api";
import {useToast} from "../../../common/contexts/ToastContext";
import {useAdminConfirmDialog} from "../../../common/hooks/useAdminConfirmDialog";
import {Collection} from '../types/Collection';
import {ArtisticObjectListing} from '../../artisticobjects/types/ArtisticObject';
import {PlaceListing} from '../../artisticobjects/types/Place';
import {getValueWithFallback} from '../../../common/types/StringTranslations';

interface TileWithInfo extends Tile {
  info?: string;
}

const TilesList: React.FC = () => {
  const {t, i18n} = useTranslation();
  const {showError, showSuccess} = useToast();
  const {showConfirmDialog} = useAdminConfirmDialog();
  const [tiles, setTiles] = useState<TileWithInfo[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [artisticObjects, setArtisticObjects] = useState<ArtisticObjectListing[]>([]);
  const [places, setPlaces] = useState<PlaceListing[]>([]);
  const navigate = useNavigate();
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    name: {value: null, matchMode: FilterMatchMode.CONTAINS},
    type: {value: null, matchMode: FilterMatchMode.EQUALS}
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (): Promise<void> => {
    const [tilesData, collectionsData, artisticObjectsData, placesData] = await Promise.all([
      tilesService.getAllTiles(),
      collectionsService.getAllCollections(),
      artisticObjectsService.getAllArtisticObjects(),
      placesService.getAllPlaces()
    ]);

    setCollections(collectionsData);
    setArtisticObjects(artisticObjectsData);
    setPlaces(placesData);

    const tilesWithInfo = tilesData.map(tile => ({
      ...tile,
      info: getInfoForTile(tile, collectionsData, artisticObjectsData, placesData)
    }));

    setTiles(tilesWithInfo);
  };

  const getInfoForTile = (
    tile: Tile,
    collections: Collection[],
    artisticObjects: ArtisticObjectListing[],
    places: PlaceListing[]
  ): string => {
    switch (tile.type) {
      case TileType.COLLECTION:
        const collection = collections.find(c => c.id === tile.collectionId);
        return collection ? getValueWithFallback(collection.title, i18n.language) : '-';
      case TileType.ARTISTIC_OBJECT:
        const ao = artisticObjects.find(ao => ao.id === tile.artisticObjectId);
        return ao?.name || '-';
      case TileType.PLACE:
        const place = places.find(p => p.id === tile.placeId);
        return place?.name || '-';
      case TileType.PATH:
        return tile.path || '-';
      default:
        return '-';
    }
  };

  const handleDelete = async (tile: TileWithInfo): Promise<void> => {
    const tileName = getValueWithFallback(tile.name, i18n.language);
    showConfirmDialog({
      message: t('admin.common.messages.confirmDeleteWithName', {name: tileName}),
      accept: async () => {
        await handleError(
          async () => {
            await tilesService.deleteTile(tile.id);
            await fetchData();
          },
          t('admin.tiles.errors.deleting')
        );
      }
    });
  };

  const handleEdit = (tile: TileWithInfo) => {
    navigate(`${tile.id}/edit`);
  };

  const handleCreateNew = () => {
    navigate('new');
  };

  const handleRowReorder = async (e: DataTableRowReorderEvent<DataTableValueArray>): Promise<void> => {
    const reorderedTiles = e.value as TileWithInfo[];
    setTiles(reorderedTiles);

    const tileIds = reorderedTiles.map(tile => tile.id);
    await handleError(
      async () => {
        await tilesService.reorderTiles(tileIds);
      },
      t('admin.tiles.errors.reordering'),
      () => {
        fetchData();
      }
    );
  };

  const typeBodyTemplate = (tile: TileWithInfo) => {
    const typeLabels: Record<TileType, string> = {
      [TileType.COLLECTION]: t('admin.tiles.types.collection'),
      [TileType.ARTISTIC_OBJECT]: t('admin.tiles.types.artisticObject'),
      [TileType.PLACE]: t('admin.tiles.types.place'),
      [TileType.PATH]: t('admin.tiles.types.path')
    };
    return <span>{typeLabels[tile.type]}</span>;
  };

  return (
    <>
      <AdminFormHeader
        title={t('admin.tiles.title.list')}
        subtitle={t('admin.tiles.subtitle.list')}
      />

      <AdminButton
        onClick={handleCreateNew}
        variant="black"
        label={t('admin.tiles.actions.createNew')}
      />

      <DataTable
        value={tiles}
        stripedRows
        paginator
        rows={10}
        filterDisplay="row"
        filters={filters}
        reorderableRows
        onRowReorder={handleRowReorder}
        showGridlines
      >
        <Column rowReorder style={{width: '3rem'}}/>
        <Column
          sortField="name"
          header={t('admin.common.columns.name')}
          sortable
          filter
          filterField="name"
          filterPlaceholder={t('admin.common.filters.searchByName')}
          showFilterMenu={false}
          showClearButton={false}
          body={(tile: TileWithInfo) => (
            <strong>{getValueWithFallback(tile.name, i18n.language)}</strong>
          )}/>
        <Column
          sortField="type"
          header={t('admin.common.columns.type')}
          sortable
          filter
          filterField="type"
          filterPlaceholder={t('admin.tiles.filters.filterByType')}
          showFilterMenu={false}
          showClearButton={false}
          body={typeBodyTemplate}
        />
        <Column
          header={t('admin.tiles.columns.info')}
          body={(tile: TileWithInfo) => (
            <span>{tile.info}</span>
          )}/>
        <Column
          header={t('admin.common.columns.actions')}
          body={(tile: TileWithInfo) => (
            <div className="flex flex-nowrap gap-2">
              <Button
                onClick={() => handleEdit(tile)}
                icon="fa fa-edit"
                severity="info"
                size="small"
                outlined
              />
              <Button
                onClick={() => handleDelete(tile)}
                icon="fa fa-trash"
                severity="danger"
                size="small"
                outlined
              />
            </div>
          )}/>
      </DataTable>
    </>
  );
};

export default TilesList;


